import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Calendar, User, LogIn, BadgeCheck } from "lucide-react";
import { Wordmark } from "./Logo";
import { daysLeft } from "./Countdown";
import { api } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/features", label: "Features", id: "features" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/electric", label: "Electric", id: "electric" },
  { to: "/suv", label: "SUV", id: "suv" },
  { to: "/ishustar", label: "Ishustar", id: "ishustar" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [account, setAccount] = useState(undefined);
  const [bookingInfo, setBookingInfo] = useState(null);
  const location = useLocation();

  const loadAccount = () => {
    api
      .get("/auth/me", { withCredentials: true })
      .then(({ data }) => {
        setAccount(data);
        if (data.role !== "admin") {
          api
            .get("/account/bookings", { withCredentials: true })
            .then(({ data: bks }) => {
              setBookingInfo({
                count: bks.length,
                confirmed: bks.filter((b) => b.status === "confirmed").length,
              });
            })
            .catch(() => setBookingInfo(null));
        }
      })
      .catch(() => setAccount(null));
  };

  useEffect(() => {
    loadAccount();
  }, [location.pathname, open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`glass-nav fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-white/10" : "border-white/5"
      }`}
      data-testid="site-nav"
    >
      <div className="bg-gradient-to-r from-[#e6b800] via-[#ffc700] to-[#e6b800] text-[#070709]" data-testid="delivery-banner">
        <p className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-5 py-1.5 text-center font-mono2 text-[10px] font-bold uppercase tracking-[0.15em] sm:text-[11px]">
          <Calendar size={12} className="shrink-0" />
          Book today · 15% OFF · Delivery January 2028 · Token from ₹10,000 · {daysLeft()} दिन बचे
        </p>
      </div>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-link-home-logo" aria-label="ISHU home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-mono2 text-[12px] uppercase tracking-[0.22em] transition-colors duration-200 ${
                  isActive ? "text-[#e6b800]" : "text-neutral-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/account"
            data-testid="nav-account-link"
            className="group inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white transition-colors duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
          >
            <User size={15} className="text-[#e6b800]" />
            Account
            {account?.role !== "admin" && bookingInfo?.count > 0 && (
              <span
                className="rounded-full bg-[#e6b800]/15 px-2 py-0.5 text-[10px] font-bold text-[#e6b800]"
                data-testid="nav-account-booking-count"
              >
                {bookingInfo.count}
              </span>
            )}
          </Link>
          <Link
            to="/booking"
            data-testid="nav-reserve-cta"
            className="group inline-flex items-center gap-1.5 rounded-full bg-[#e6b800] px-5 py-2.5 text-sm font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_28px_rgba(230,184,0,0.4)]"
          >
            Reserve Now
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid={open ? "nav-mobile-close" : "nav-mobile-open"}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28 }}
            className="overflow-hidden border-t border-white/10 bg-[#070709]/95 md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 font-mono2 text-[13px] uppercase tracking-[0.22em] ${
                      isActive ? "bg-white/5 text-[#e6b800]" : "text-neutral-300"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {account !== undefined && account !== null && account.role !== "admin" ? (
                <Link
                  to="/account"
                  data-testid="nav-mobile-account"
                  className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 text-[#e6b800]">
                      <User size={15} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-white">{account.name || "Account"}</span>
                      <span className="block truncate text-[11px] text-neutral-400" data-testid="nav-mobile-booking-info">
                        {bookingInfo
                          ? `${bookingInfo.count} booking${bookingInfo.count === 1 ? "" : "s"} · ${bookingInfo.confirmed} confirmed`
                          : "मेरी bookings देखें"}
                      </span>
                    </span>
                  </span>
                  {bookingInfo?.confirmed > 0 && <BadgeCheck size={16} className="shrink-0 text-[#e6b800]" />}
                </Link>
              ) : account !== undefined && account !== null && account.role === "admin" ? (
                <Link
                  to="/account"
                  data-testid="nav-mobile-account"
                  className="mt-2 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 text-[#e6b800]">
                    <User size={15} />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">Owner Account</span>
                    <span className="block text-[11px] text-neutral-400">Admin dashboard</span>
                  </span>
                </Link>
              ) : account !== undefined && account === null ? (
                <Link
                  to="/account"
                  data-testid="nav-mobile-account"
                  className="mt-2 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 text-[#e6b800]">
                    <LogIn size={15} />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">Customer Login</span>
                    <span className="block text-[11px] text-neutral-400">Login / नया account — bookings देखें</span>
                  </span>
                </Link>
              ) : null}
              <Link
                to="/booking"
                data-testid="nav-mobile-reserve-cta"
                className="mt-2 rounded-full bg-[#e6b800] px-5 py-3 text-center text-sm font-bold text-[#070709]"
              >
                Reserve Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}